/**
 * The authorization model, transcribed once (docs/07-implementation-roadmap.md F1.7,
 * §1.3; docs/04 §1.3).
 *
 * **This is the only file in the app where a role name appears.** Screens ask
 * `can(user, ability, resource)`; none of them writes `role === 'STAFF'`. That
 * is what makes "decide who can do what later" cost one file instead of an
 * excavation through forty components — and it is why `policy.test.ts` sweeps
 * every ability against every role on every PR (F-DoD-5).
 *
 * **The server is the authority; this is a copy.** The client cannot widen
 * anything — the guards shipped in backend Sprints 2–5 have the last word. What
 * this file buys is that staff are never shown an action the server will
 * refuse. The table below is therefore transcribed from the **guards as they are
 * written** (`@Roles(...)` on each controller), not from the summary table in
 * docs/04 §1.3 — which had drifted from them in two places, was corrected in
 * Sprint F1 because this transcription caught it, and now agrees. See
 * `DEVIATIONS_FROM_DOCS_04` at the bottom, which is empty and says so.
 *
 * MEMBER is deliberately absent from every row. This is the staff back office
 * (docs/07 §0); a MEMBER account is turned away at the door with an explanation
 * rather than being given a session that can reach nothing (see
 * `isBackOfficeRole`). Listing MEMBER here would invite a screen to imagine it
 * has a member-facing mode, which belongs to the deferred mobile client.
 *
 * **The human-readable view of this table is `docs/10-roles-and-features.md`** —
 * the same rules as capabilities and screens, for the F7.9 review. It is a view,
 * not a fifth copy: `policy-doc-sentinel.test.ts` parses its tables and checks
 * every role cell against `can()`, so this file stays the only place a role name
 * is *decided*.
 */

/** Every role the access token can carry (docs/04 §1.1). */
export type Role = 'SUPER_ADMIN' | 'STAFF' | 'REGION_COORDINATOR' | 'PASTOR' | 'MEMBER';

/** The four roles this app is built for (docs/07 §0). */
export type StaffRole = Exclude<Role, 'MEMBER'>;

export const STAFF_ROLES: readonly StaffRole[] = ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR', 'PASTOR'];

export function isBackOfficeRole(role: Role): role is StaffRole {
  return (STAFF_ROLES as readonly Role[]).includes(role);
}

/**
 * How each role is named to the people who hold it. Here rather than in the
 * header component for the same reason as everything else in this file: a role
 * name written anywhere else is a role name that can drift.
 */
export const ROLE_LABELS: Record<Role, string> = {
  SUPER_ADMIN: 'Administrator',
  STAFF: 'Staf',
  REGION_COORDINATOR: 'Koordinator Wilayah',
  PASTOR: 'Pendeta',
  MEMBER: 'Jemaat',
};

/**
 * One entry per **authorization decision** the back office has to make, named
 * for what a person does rather than for the HTTP verb — a screen asks "may I
 * change this member's status?", not "may I POST to /members/:id/status?".
 *
 * Mostly that is one entry per endpoint, and it was originally written that
 * way. `members.setDeceased` is why the wording is broader: the server gates it
 * on the *body* of a call to an endpoint the caller may otherwise use, so a
 * rule can be narrower than the route it lives on. An entry earns its place
 * here by being a question a screen must ask before drawing something, not by
 * having a URL.
 */
export type Ability =
  | 'members.list'
  | 'members.read'
  | 'members.create'
  | 'members.update'
  | 'members.updateNotes'
  | 'members.delete'
  | 'members.changeStatus'
  | 'members.setDeceased'
  | 'members.markNoteSensitive'
  | 'members.requestStatusChange'
  | 'members.readStatusHistory'
  | 'members.convert'
  | 'members.inviteLogin'
  | 'members.export'
  | 'members.birthdays'
  | 'members.anniversaries'
  | 'members.verify'
  | 'members.readDocuments'
  | 'members.manageDocuments'
  | 'families.list'
  | 'families.read'
  | 'families.create'
  | 'families.update'
  | 'families.changeRegion'
  | 'families.delete'
  | 'families.manageMembers'
  | 'families.verify'
  | 'regions.list'
  | 'regions.manage'
  | 'changeRequests.list'
  | 'changeRequests.decide'
  | 'users.list'
  | 'users.invite'
  | 'users.update'
  | 'users.suspend'
  | 'users.cancelInvite'
  | 'church.read'
  | 'church.update'
  | 'auditLogs.read'
  | 'reports.read'
  | 'reports.readChurchWide';

interface Rule {
  /** The roles whose guard lets this call through. */
  roles: readonly StaffRole[];
  /**
   * Whether a REGION_COORDINATOR is additionally confined to their own region
   * for this call (docs/04 §1.3, §3.1). A list endpoint is *not* marked: the
   * server narrows the query rather than refusing it, so a coordinator asking
   * for the list is always allowed to ask.
   */
  regionScoped?: true;
  /**
   * Roles granted only where a church setting is on. Only
   * `members.changeStatus` for PASTOR has one today.
   */
  conditionalRoles?: readonly StaffRole[];
  /**
   * The `GET /church/client-config` key that grants `conditionalRoles`.
   *
   * Named here rather than hard-coded in `useCan` so the pattern docs/07 §1.3
   * points at — one boolean per rule the church actually wants to vary — costs
   * a row in this table and nothing else. `can()` still takes the answer as a
   * plain option, so the policy stays a pure function with no idea where church
   * settings come from.
   */
  conditionalSetting?: ConditionalSetting;
}

/**
 * Client-config keys that can widen the matrix. A union rather than
 * `keyof ChurchClientConfigDto` so that adding a presentation-only key to the
 * contract does not silently become an authorization lever.
 */
export type ConditionalSetting = 'pastorsCanApproveStatusChanges' | 'allowCoordinatorChurchWideAggregates';

/**
 * The matrix. Each row is the `@Roles(...)` decorator on the shipped
 * controller, so a disagreement between this table and the server is a bug in
 * exactly one of the two and reviewable as such.
 */
const RULES: Record<Ability, Rule> = {
  // Members — src/members/members.controller.ts
  'members.list': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR', 'PASTOR'] },
  'members.read': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR', 'PASTOR'], regionScoped: true },
  'members.create': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'] },
  'members.update': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'], regionScoped: true },
  'members.updateNotes': { roles: ['SUPER_ADMIN', 'STAFF', 'PASTOR'] },
  'members.delete': { roles: ['SUPER_ADMIN'] },
  // PASTOR only where `pastorsCanApproveStatusChanges` is on (docs/04 §2).
  // F3 put that key on `GET /church/client-config` (docs/07 §1.2 option 1), so
  // the question is now answerable and `useCan` answers it — a pastor is shown
  // whichever of the two actions their church actually gives them, and never a
  // button the guard would refuse.
  'members.changeStatus': {
    roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'],
    conditionalRoles: ['PASTOR'],
    conditionalSetting: 'pastorsCanApproveStatusChanges',
    regionScoped: true,
  },
  // Recording a death, or reversing one, inside a call to `/status`
  // (docs/01 §3 rule 4, `assertActorMayFinalize`). Its own row rather than
  // borrowing `members.delete`, which the status dialog used to ask instead:
  // both are SUPER_ADMIN-only today, and nothing makes them move together.
  // Whoever widens delete at F7.9 would otherwise widen this in the same
  // breath, and the server — which checks the role directly, not the ability —
  // would answer 403 on the most consequential form in the app.
  //
  // Narrower than the endpoint it lives on: `members.changeStatus` says whether
  // the dialog opens at all, this says whether one option inside it is real.
  'members.setDeceased': { roles: ['SUPER_ADMIN'] },
  // Narrower than the route it lives on, like `members.setDeceased` above:
  // `POST /members/:id/notes` admits a REGION_COORDINATOR wherever the church
  // has granted them `members.updateNotes`, and the server then refuses anyone
  // but these three from marking the entry sensitive. It says whether one
  // checkbox inside the composer is real, not whether the composer opens.
  //
  // The reason is the read side: a coordinator never sees a sensitive entry
  // (`02` rule 6), so offering them the box would offer a way to write
  // something they could not then read back.
  'members.markNoteSensitive': { roles: ['SUPER_ADMIN', 'STAFF', 'PASTOR'] },
  'members.requestStatusChange': { roles: ['PASTOR'] },
  'members.readStatusHistory': {
    roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR', 'PASTOR'],
    regionScoped: true,
  },
  'members.convert': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'], regionScoped: true },
  'members.inviteLogin': { roles: ['SUPER_ADMIN', 'STAFF'] },
  'members.export': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'] },
  'members.birthdays': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'] },
  'members.anniversaries': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'] },
  // The coordinator belongs on this row more than on most: verification is the
  // one job in the app that needs somebody who can go and look, and a
  // coordinator is a neighbour in the region they coordinate (`02` rule 9a).
  //
  // Region-scoped, so the button is drawn only for a record they can reach.
  // What this row cannot say is *which sources* they may record — the server
  // narrows a coordinator to MEMBER and COORDINATOR by reading the body, so
  // that rule is narrower than the route and has no cell, like
  // `members.setDeceased`. The dialog asks `verificationSourcesFor` instead.
  'members.verify': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'], regionScoped: true },
  // The certificates behind the register facts (docs/02 §1.3b). Read by every
  // role that reads the record — a pastor included, since a baptism certificate
  // is sacramental record rather than pastoral confidence — and changed by the
  // roles that edit it. Uploading at Atestasi Masuk rides on `members.create`
  // instead, because there is no record yet to hold the file.
  'members.readDocuments': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR', 'PASTOR'], regionScoped: true },
  'members.manageDocuments': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'], regionScoped: true },

  // Families — src/families/families.controller.ts
  'families.list': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'] },
  'families.read': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR', 'PASTOR'], regionScoped: true },
  'families.create': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'] },
  'families.update': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'], regionScoped: true },
  // Moving a household between regions moves who can see its members
  // (docs/04 §3.1 rule 3), so it is not something a coordinator may do to their
  // own boundary.
  'families.changeRegion': { roles: ['SUPER_ADMIN', 'STAFF'] },
  'families.delete': { roles: ['SUPER_ADMIN'] },
  'families.manageMembers': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'], regionScoped: true },
  'families.verify': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'], regionScoped: true },

  // Regions — src/regions/regions.controller.ts
  // A coordinator may *list* regions (they get their own back, and pickers need
  // it); creating, renaming and deleting one is Super Admin work.
  'regions.list': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'] },
  'regions.manage': { roles: ['SUPER_ADMIN'] },

  // Change requests — src/change-requests/change-requests.controller.ts
  'changeRequests.list': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'] },
  'changeRequests.decide': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'], regionScoped: true },

  // Users — src/users/users.controller.ts
  'users.list': { roles: ['SUPER_ADMIN'] },
  'users.invite': { roles: ['SUPER_ADMIN'] },
  'users.update': { roles: ['SUPER_ADMIN'] },
  'users.suspend': { roles: ['SUPER_ADMIN'] },
  // Wider than every other `users.*` rule. An unredeemed invitation is a claim
  // on an email address and nothing else, and cancelling one is how a mistyped
  // address gets fixed — so it belongs to the people who issue invitations.
  // The narrowing the server adds cannot be expressed here and is not tried:
  // only a MEMBER invite, only in a coordinator's own region.
  //
  // **The screen is the member record, not `/pengguna`**, which these two roles
  // cannot open. An ability with nowhere to invoke it is `10` §9.1 item 1, and
  // once was enough.
  'users.cancelInvite': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'], regionScoped: true },

  // Church — src/church/church.controller.ts. `GET /church/client-config` is
  // deliberately absent: every authenticated user may call it, which is what
  // makes it the only settings endpoint the shell is allowed to depend on
  // (F1.9).
  'church.read': { roles: ['SUPER_ADMIN', 'STAFF'] },
  'church.update': { roles: ['SUPER_ADMIN'] },

  // Audit log — src/audit/audit-logs.controller.ts
  'auditLogs.read': { roles: ['SUPER_ADMIN'] },

  // Reports — src/reports/reports.controller.ts (F6.1). PASTOR is absent from
  // the controller's `@Roles(...)`, which is the whole reason this ability
  // exists rather than the dashboard being shown to everyone who lands on the
  // home screen: a pastor reaches the congregation through member records, and
  // reporting is a staff and coordinator workflow (docs/01 §7).
  'reports.read': { roles: ['SUPER_ADMIN', 'STAFF', 'REGION_COORDINATOR'] },
  // The **church-wide scope**, not the screen — the second rule in this file
  // that is narrower than its endpoint, after `members.setDeceased`.
  //
  // A coordinator is never refused the report: it answers for their own region
  // by default. What `allowCoordinatorChurchWideAggregates` decides is whether
  // they may ask for `scope=church`, and the server answers 403 when they may
  // not (docs/04 §6a). So this gates one control inside the screen, and getting
  // it wrong shows a toggle that fails rather than a screen that is missing.
  'reports.readChurchWide': {
    roles: ['SUPER_ADMIN', 'STAFF'],
    conditionalRoles: ['REGION_COORDINATOR'],
    conditionalSetting: 'allowCoordinatorChurchWideAggregates',
  },
};

/** What `can()` needs to know about the signed-in user. */
export interface PolicySubject {
  role: Role;
  /** Present only for REGION_COORDINATOR (docs/04 §1.1). */
  regionId: string | null;
}

/**
 * The resource being acted on, when the caller knows its region. Omit it and a
 * region-scoped ability answers on the role alone — which is the right answer
 * for "should this button exist at all", and deliberately optimistic for "may
 * this particular record be touched": the server owns that judgement and
 * answers 403 (F1.10 turns that into a legible screen rather than a toast).
 */
export interface PolicyResource {
  regionId?: string | null;
}

/** The setting a conditional grant on this ability depends on, if it has one. */
export function conditionalSettingFor(ability: Ability): ConditionalSetting | undefined {
  return RULES[ability].conditionalSetting;
}

/**
 * The subject's **own** resolved answers for the abilities their church may
 * configure, from `GET /church/client-config` (docs/07 P4).
 *
 * A separate mechanism from `conditionalRolesEnabled`, and the two must not be
 * merged. A conditional role is a *rule in the code* that one `Church.settings`
 * flag switches on for one named role; this is a rule the church may move for
 * any editable role, stored in a table, with no code path naming the role at
 * all. `10` §4 keeps them visually distinct too — `⚙️` means the first and is
 * deliberately not used for the second.
 *
 * **It describes one subject: the signed-in user.** The server sends only the
 * caller's own row, so passing it alongside a different `user` would answer
 * about the wrong person. `useCan` binds the two together, and nothing else
 * should pass this.
 */
export type PermissionOverlay = Partial<Record<Ability, boolean>>;

export function can(
  user: PolicySubject | null | undefined,
  ability: Ability,
  resource?: PolicyResource,
  options?: { conditionalRolesEnabled?: boolean; permissions?: PermissionOverlay },
): boolean {
  if (!user) return false;
  if (!isBackOfficeRole(user.role)) return false;

  const rule = RULES[ability];
  const configured = options?.permissions?.[ability];
  const granted =
    configured ??
    (rule.roles.includes(user.role) ||
      (options?.conditionalRolesEnabled === true && (rule.conditionalRoles?.includes(user.role) ?? false)));
  if (!granted) return false;

  if (rule.regionScoped && user.role === 'REGION_COORDINATOR') {
    // Fail closed on a coordinator carrying no region, exactly as the server
    // does (`regionScopeWhere` in src/common/utils/region-scope.util.ts): the
    // wrong default here is "everything in the church".
    if (!user.regionId) return false;
    if (resource?.regionId !== undefined && resource.regionId !== user.regionId) return false;
  }

  return true;
}

/**
 * Whether a 403 from the server, for something the policy said was allowed, can
 * only be the region boundary.
 *
 * The server's message for it is the literal string `'Region mismatch'`, which
 * is too thin a thread to hang a screen on. This asks the structural question
 * instead: a coordinator has full CRUD inside their own region (docs/01 §1.1),
 * so when the client believed the action was permitted and the server refused,
 * the region is the only remaining explanation. That is what F1.10's dedicated
 * "di luar wilayah Anda" screen keys on.
 */
export function isRegionBoundaryRefusal(
  user: PolicySubject | null | undefined,
  requirement: AbilityRequirement,
): boolean {
  if (!user || user.role !== 'REGION_COORDINATOR') return false;
  // Any one of them qualifying is enough, for the same reason any one of them
  // would have let the person in: a coordinator refused a screen they hold one
  // of its abilities for was stopped by the boundary and nothing else.
  return abilitiesOf(requirement).some(
    (ability) => RULES[ability].regionScoped === true || RULES[ability].roles.includes('REGION_COORDINATOR'),
  );
}

/**
 * Whether everything this user sees is already narrowed to a single region by
 * the server (docs/07-implementation-roadmap.md F2.3, docs/04 §2).
 *
 * `GET /members` filters a REGION_COORDINATOR to their own region **whatever
 * `regionId` is passed** — it narrows the query rather than refusing it, which
 * is why `members.list` is not marked `regionScoped` above. The consequence for
 * a screen is that offering a region control to a coordinator would imply a
 * choice they do not have: every option but one returns their own region's
 * members anyway, which reads as broken filtering rather than as a boundary.
 *
 * It lives here, and not as a `role === 'REGION_COORDINATOR'` check in the
 * filter bar, for the same reason as everything else in this file.
 */
export function isRegionConfined(user: PolicySubject | null | undefined): boolean {
  return user?.role === 'REGION_COORDINATOR';
}

/**
 * What a screen requires: one ability, or several of which **any one** is
 * enough.
 *
 * A list is not a way to write a looser gate. It is for a screen that two
 * different jobs reach by two different rules — `/wilayah` is the case that
 * introduced it, where an Administrator arrives to manage regions and a Staf
 * arrives to move a coordinator between them (docs/10 §9.1 item 1). Writing
 * that as one ability would mean either widening `regions.manage`, which grants
 * create and delete to get at a move, or gating the screen on an ability it
 * does not call. Both are the mismatch this file exists to prevent.
 *
 * Every ability in a list must still be refused independently by the server —
 * the client only ever agrees with the guards, and each control on the screen
 * asks `can()` for its own rule regardless of what let the person in.
 */
export type AbilityRequirement = Ability | readonly Ability[];

/** The abilities a requirement names, whichever shape it was written in. */
export function abilitiesOf(requirement: AbilityRequirement): readonly Ability[] {
  return typeof requirement === 'string' ? [requirement] : requirement;
}

/** `can()` over a requirement: true when any one of the abilities passes. */
export function canAny(
  user: PolicySubject | null | undefined,
  requirement: AbilityRequirement,
  resource?: PolicyResource,
  options?: { conditionalRolesEnabled?: boolean; permissions?: PermissionOverlay },
): boolean {
  return abilitiesOf(requirement).some((ability) => can(user, ability, resource, options));
}

/**
 * The collapsible sections of the sidebar.
 *
 * A group is a plain id and a label, deliberately with no React in it and no
 * abilities of its own: **a group is never a gate.** It is visible exactly when
 * one of its entries is, which falls out of filtering the list rather than
 * being decided anywhere — so a group can no more hide a screen from somebody
 * entitled to it than it can reveal one.
 *
 * The id is a menu key rather than a route: nothing is at `Data Jemaat`, and a
 * path-shaped key would eventually be treated as one.
 */
export const NAV_GROUPS = {
  /** Everything the office keeps *about the congregation* — the people, their households, where they live, and the paperwork that moves them between those. */
  dataJemaat: 'Data Jemaat',
  /** What the office reads *off* that record rather than changes in it — the rosters and registers it prints. */
  laporan: 'Laporan',
} as const;

export type NavGroupId = keyof typeof NAV_GROUPS;

/** A sidebar entry. `ability` is what decides whether the role ever sees it. */
export interface NavItem {
  path: string;
  label: string;
  /** `null` for the entries every staff role reaches. */
  ability: AbilityRequirement | null;
  /**
   * The section it folds into, if any. Entries sharing a group must be
   * **contiguous** in `NAV_ITEMS` — the shell renders the group where its first
   * member sits, so a stray entry in the middle would silently jump position.
   */
  group?: NavGroupId;
}

/**
 * The navigation, in the order the office reads it (F1.8).
 *
 * Each entry names the ability its screen is built on, so the sidebar and the
 * screen can never disagree about who may be there — and the whole sidebar
 * follows from `policy.ts` rather than from a role check in a layout component.
 */
export const NAV_ITEMS: readonly NavItem[] = [
  { path: '/', label: 'Beranda', ability: null },
  // ── Data Jemaat ────────────────────────────────────────────────────────────
  // Six entries, and half the sidebar, folded into one section. Flat they
  // pushed Pengguna, Laporan and the two admin screens off the first screenful
  // on the 1366×768 laptops the office uses, so the entries somebody reaches
  // once a week were the ones you had to scroll for.
  //
  // They are a real category rather than a convenient six: every one of them is
  // the congregation's own record — who is in it, which household, which
  // region, and the paperwork that moves people between those. Nothing outside
  // the group is about a member at all.
  { path: '/jemaat', label: 'Jemaat', ability: 'members.list', group: 'dataJemaat' },
  { path: '/keluarga', label: 'Keluarga', ability: 'families.list', group: 'dataJemaat' },
  // The two attestation desks. Each is a task the office already performed
  // through the general screens; naming them is what makes it obvious *who* a
  // transfer is about to change, which typing one member at a time never did.
  //
  // Gated on the abilities the work itself requires rather than on new rules of
  // their own: arriving is a member creation, leaving is a bulk status change.
  // So a coordinator reaches both and stays region-scoped by the same machinery
  // that scopes everything else.
  { path: '/atestasi-masuk', label: 'Atestasi Masuk', ability: 'members.create', group: 'dataJemaat' },
  { path: '/atestasi-keluar', label: 'Atestasi Keluar', ability: 'members.changeStatus', group: 'dataJemaat' },
  { path: '/permintaan-perubahan', label: 'Permintaan Perubahan', ability: 'changeRequests.list', group: 'dataJemaat' },
  // One job reaches this screen now: managing regions and appointing their
  // coordinators. It briefly took a second ability, `users.assignRegion`, so a
  // Staf could move a coordinator between regions from here — that action is
  // gone (a coordinator moves like anyone else, from the family or member
  // screen), and the gate narrowed back with it.
  { path: '/wilayah', label: 'Wilayah', ability: 'regions.manage', group: 'dataJemaat' },
  // ── end Data Jemaat ────────────────────────────────────────────────────────
  { path: '/pengguna', label: 'Pengguna', ability: 'users.list' },
  // ── Laporan ────────────────────────────────────────────────────────────────
  // Four reports, each a destination of its own and each gated on the ability
  // *its* endpoint needs — the birthday roster on `members.birthdays`, the
  // anniversary roster on `members.anniversaries`, the attestation register and
  // the register of deaths on `reports.read`. They admit the same three roles
  // today and are free to stop; a section gated on one of them would be a
  // screen gated on an ability it does not call.
  //
  // Neither sits at `/laporan` itself, and that is deliberate. The shell picks
  // the active entry with `startsWith`, so a parent path beside its own child
  // would match first and light up the wrong row — `/laporan` redirects to the
  // roster instead, which keeps every link anybody has already bookmarked.
  { path: '/laporan/ulang-tahun', label: 'Ulang Tahun', ability: 'members.birthdays', group: 'laporan' },
  {
    path: '/laporan/ulang-tahun-pernikahan',
    label: 'Ulang Tahun Pernikahan',
    ability: 'members.anniversaries',
    group: 'laporan',
  },
  { path: '/laporan/atestasi', label: 'Atestasi', ability: 'reports.read', group: 'laporan' },
  { path: '/laporan/kematian', label: 'Kematian', ability: 'reports.read', group: 'laporan' },
  // ── end Laporan ────────────────────────────────────────────────────────────
  { path: '/pengaturan', label: 'Pengaturan Gereja', ability: 'church.read' },
  { path: '/catatan-audit', label: 'Catatan Audit', ability: 'auditLogs.read' },
];

/**
 * `options` is threaded through to `can()` rather than omitted, against the day
 * a nav entry is gated on a configurable ability. `/wilayah` was that entry for
 * a while, on `users.assignRegion`; no entry is today, since the remaining
 * editable ability sits on a screen everyone with it can already open. Keeping
 * the overlay wired anyway is deliberate: a sidebar that ignored it would show
 * an entry the server had stopped allowing — or hide one it had started
 * allowing, which is the "why is it missing for me" bug that is hardest to
 * report and hardest to reproduce.
 */
export function navigationFor(
  user: PolicySubject | null | undefined,
  options?: { conditionalRolesEnabled?: boolean; permissions?: PermissionOverlay },
): NavItem[] {
  if (!user || !isBackOfficeRole(user.role)) return [];
  return NAV_ITEMS.filter((item) => item.ability === null || canAny(user, item.ability, undefined, options));
}

/**
 * The destination a pathname is at or inside — `/jemaat/abc` is `/jemaat`.
 *
 * **Whole segments, never a bare string prefix.** `/laporan/ulang-tahun` is the
 * start of `/laporan/ulang-tahun-pernikahan`, and a `startsWith` match lit up
 * the birthday report while somebody stood on the anniversary one, because the
 * birthday entry comes first in the list. The root is never matched here: it
 * would be inside every page, so a caller that wants it matches it exactly.
 */
export function activeNavItem<T extends Pick<NavItem, 'path'>>(items: readonly T[], pathname: string): T | undefined {
  return items.find((item) => item.path !== '/' && (pathname === item.path || pathname.startsWith(`${item.path}/`)));
}

/**
 * Where this file knowingly disagrees with the **summary table** in docs/04
 * §1.3, because the shipped guards do.
 *
 * **Empty, and the emptiness is the assertion** (F7.1, docs/10 §9.2). It held
 * two entries — §1.3 granting STAFF Users and Audit Log access no controller
 * gives them — and both were real when written: this file's transcription is
 * what surfaced them in Sprint F1. §1.3's STAFF row was corrected to **none**
 * for both in that same sprint, at which point the deviations stopped existing
 * and this list should have emptied with them. It did not, and neither did
 * `DOCS_04_SUMMARY` in `policy.test.ts` — so the sweep spent five sprints
 * comparing `policy.ts` against a stale copy of a table it had already fixed,
 * finding exactly the two disagreements it expected, and passing. A check that
 * cannot fail is not a check.
 *
 * Kept rather than deleted, for the same reason the sweep is: the day a guard
 * and §1.3 disagree again, one entry here is how that gets recorded instead of
 * argued about, and an *unrecorded* disagreement fails the build. The export
 * being empty is what says the two tables currently agree.
 */
export const DEVIATIONS_FROM_DOCS_04: readonly { ability: Ability; role: StaffRole; note: string }[] = [];
